// The prompt the extension's template editor sends for "Generate with AI",
// kept word for word so both produce the same kind of template. It goes to the
// same server route, POST /api/extension/completion.

// Keeps the prompt under the completion route's 20,000-character limit.
const MAX_CURRENT_BODY_CHARS = 4000

const currentTemplateText = template =>
	template.subject || template.body
		? `
Current template that user did not like, wants a completely different option:
{
  "name": "${template.name}",
  "subject": "${template.subject}",
  "body": "${template.body.slice(0, MAX_CURRENT_BODY_CHARS)}"
}

Please generate a DIFFERENT template with a new creative style. Do not copy the structure or phrasing of the current template. Be creative and unique.
`
		: ''

export const buildTemplatePrompt = currentTemplate => `You are a CARRIER generating a short email template to inquire about a LOAD posted on a loadboard.
IMPORTANT CONTEXT: YOU are the carrier with the equipment. You are inquiring about THEIR load. Do NOT ask if equipment is available - YOU have the equipment!

${currentTemplateText(currentTemplate)}
Key points:
- Write a short, conversational email asking about the loads status and pay.
- YOU have the {{equipment_type}} - don't ask about equipment availability
- Ask about the LOAD availability and pay
- All variables MUST use double curly braces format like {{variable_name}}
- NEVER use placeholders like [Name] or [Carrier]
- Template name should be simple and static (NO variables)
- Use proper email formatting with <br/> tags (no backslashes)

**CRITICAL CHARACTER ENCODING RULES:**
- ONLY use standard ASCII characters (a-z, A-Z, 0-9, and basic punctuation: . , ? ! : " ')
- DO NOT use: em dashes (—), en dashes (–), fancy quotes (" " ' '), arrows (→), or ANY Unicode/special characters
- Use regular hyphen (-) ONLY when grammatically necessary (like "{{trip_miles}}-mile"), NOT as decorative separators
- For subject lines: Keep it simple with just spaces, commas, and the word "to" between locations
- BAD: "Van Ready – {{origin}}" or "{{origin}}→{{destination}}"
- GOOD: "Van Ready {{origin}} to {{destination}}"

Style Guidelines:
- Be natural and **lexically diverse**; avoid re-using the same phrases
- Avoid too polite tonality, be direct and concise. If a phrase can be rephrased to be more direct, do so.
- Keep it concise but friendly
- Avoid being too verbose, too many words. Imagine you are sending this email to a friend.

Available variables (ONLY use these - do not create new ones):
{{origin}}
{{destination}}
{{pick_date}}
{{equipment_type}}
{{trip_miles}}
{{deadhead}} - Distance in miles from the carrier's current location to the pickup point
{{reference_id}} - The broker's reference/load ID number for this shipment

Variable Usage Notes:
- {{deadhead}}: Represents how far the carrier is from the pickup location. Use this to highlight proximity to origin when deadhead is low (e.g., "I'm only {{deadhead}} miles from {{origin}}") or to show willingness to cover the distance. This is valuable information for brokers.
- {{reference_id}}: The broker's internal load/reference ID. Include this when available to help the broker quickly identify which load you're inquiring about (e.g., "Regarding your load {{reference_id}}" or "RE: Load {{reference_id}}").

Requirements:
1. Subject: Be creative but clear, use ONLY plain ASCII text - no dashes, arrows, or special characters as separators
   - GOOD: "Van Ready {{origin}} to {{destination}}" or "Available for {{origin}} Load"
   - BAD: "Van Ready — {{origin}}" or "{{origin}} → {{destination}}"
2. Body must be properly formatted with line breaks:
   - Natural, varied greeting
   - Ask about load status and rate in a fresh way
   - Use <br/> tags for line breaks
   - ONLY plain ASCII characters throughout
3. Output a valid JSON object with 'name', 'subject', and 'body' fields
4. The 'name' field should be descriptive but simple
5. NO explanations or markdown - just the JSON

Example Styles (do not copy exactly - create new variations):
{
  "name": "Quick Load Check",
  "subject": "Ready to Move Your {{origin}} to {{destination}} Load",
  "body": "Hi there,<br/><br/>Perfect timing, I've got a {{equipment_type}} ready to roll for your {{origin}} to {{destination}} shipment on {{pick_date}}.<br/><br/>Would love to hear your rate for this {{trip_miles}} mile run.<br/><br/>Regards."
}

{
  "name":"Quick Rate Check",
  "subject":"{{equipment_type}} Ready {{origin}} to {{destination}}",
  "body":"Good day,<br/><br/>Have a {{equipment_type}} staged in {{origin}} for {{pick_date}}, set to cover your {{destination}} haul {{trip_miles}} mi.<br/><br/>Is the load still rolling? What is the rate?<br/><br/>Thanks,"
}

{
  "name": "Direct Inquiry",
  "subject": "{{equipment_type}} Available for {{origin}} Load",
  "body": "Good morning,<br/><br/>Just spotted your {{destination}} bound load. My {{equipment_type}} is positioned for your {{pick_date}} pickup in {{origin}}.<br/><br/>What is your best rate for this route?<br/><br/>Best regards"
}

{
  "name": "Proximity Highlight",
  "subject": "{{equipment_type}} Near {{origin}} for {{destination}} Load",
  "body": "Hi,<br/><br/>Saw your {{origin}} to {{destination}} load for {{pick_date}}. I'm only {{deadhead}} miles out from {{origin}} with a {{equipment_type}} ready to go.<br/><br/>Still available? What is the rate for this {{trip_miles}} mile run?<br/><br/>Thanks"
}

{
  "name": "Load Reference Check",
  "subject": "RE Load {{reference_id}} {{origin}} to {{destination}}",
  "body": "Hello,<br/><br/>Inquiring about your load {{reference_id}} from {{origin}} to {{destination}} on {{pick_date}}.<br/><br/>I have a {{equipment_type}} available and ready. What is your rate for this {{trip_miles}} mile route?<br/><br/>Thanks"
}

Create a new version of the template with your own creative style while maintaining the same structure and tone.`

// The model answers with a JSON object, sometimes wrapped in a code fence.
export const parseGeneratedTemplate = text => {
	const parsed = JSON.parse(String(text).replace(/```json\n?/g, '').replace(/```\n?/g, '').trim())
	if (!parsed.subject || !parsed.body) throw new Error('The AI response was missing a subject or body')
	return { name: parsed.name?.trim() || '', subject: parsed.subject.trim(), body: parsed.body.trim() }
}
